import dayjs from "dayjs";
import { useDatetime } from "../../../../../utils/useDatetime";
import { useFhirData } from "../../../../../utils/useFhirData";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { ProblemsActions } from "./ProblemsActions";
import { EhrMobileGenericSection } from "../../mobile/EhrMobileGenericSection";

// Tipo para item de problema processado
interface ProblemItem {
  encounterId: string | undefined;
  encounterRef: string | undefined;
  param: string;
  description: string;
  date: string;
  dateObj: Date;
  practitioner: string;
  informant: string;
  isCustom: boolean;
  hasStatus?: boolean;
}

export function Problems({
  data,
  title,
  openVisualizer,
  verifyVisualizer,
  personData,
}: any) {
  const { formatToDDMMYYYY } = useDatetime;
  const { access } = useSessionContext();

  const columns = [
    { column: "Descrição", rowName: "description" },
    { column: "CIAP2", rowName: "param" },
    { column: "Profissional", rowName: "practitioner" },
    { column: "Data", rowName: "date" },
  ];

  // ============= Funções Auxiliares =============

  /**
   * Processa dados FHIR (Condition resources)
   * Retorna array de problemas formatados
   */
  function processFhirData(data: any[]): ProblemItem[] {
    if (!data) return [];

    return data.map((el: any) => ({
      encounterId: el?.resource?.encounter?.reference?.split("/")?.[1],
      encounterRef: el?.resource?.encounter?.reference,
      param: el?.resource?.code?.coding?.[0]?.code || "-",
      description: el?.resource?.code?.coding?.[0]?.display || "",
      date: dayjs(el?.resource?.recordedDate).format("DD/MM/YYYY HH:mm"),
      dateObj: new Date(el?.resource?.recordedDate),
        practitioner:
          (useFhirData.findParticipant(el?.resource?.participant, "Practitioner")
            ?.display || "").toUpperCase(),
      informant: useFhirData.findParticipant(
        el?.resource?.participant,
        "Patient"
      )
        ? "Paciente"
        : "Profissional",
      isCustom: false,
    }));
  }

  /**
   * Extrai descrições normalizadas de uma lista de problemas
   */
  function extractDescriptions(problems: ProblemItem[]): Set<string> {
    const descriptions = new Set<string>();
    problems.forEach((item) => {
      if (item.description) {
        descriptions.add(item.description.trim().toLowerCase());
      }
    });
    return descriptions;
  }

  /**
   * Processa dados de questionários
   * Exclui problemas que já existem nos dados FHIR
   */
  function processQuestionnaireData(
    personData: any[],
    fhirDescriptions: Set<string>
  ): ProblemItem[] {
    if (!personData?.length) return [];

    // Agrupa problemas por descrição
    const problemGroups = groupQuestionnaireProblems(
      personData,
      fhirDescriptions
    );

    // Aplica lógica de filtragem par/ímpar
    return applyEvenOddLogic(problemGroups);
  }

  /**
   * Agrupa problemas de questionários por descrição
   */
  function groupQuestionnaireProblems(
    personData: any[],
    fhirDescriptions: Set<string>
  ): Record<string, ProblemItem[]> {
    const groups: Record<string, ProblemItem[]> = {};

    personData.forEach((entry: any) => {
      if (entry?.resource?.resourceType !== "QuestionnaireResponse") return;

      const resource = entry.resource;
      const encounterRef = resource?.encounter?.reference;
      const encounterId = encounterRef?.split("/")?.[1];
      const authored = resource?.authored;

      // Busca seção de problemas
      const problemsSection = resource?.item?.find(
        (section: any) => section?.text === "Lista de Problemas"
      );

      if (!problemsSection?.item) return;

      problemsSection.item.forEach((problem: any) => {
        const description = problem?.item?.[0]?.answer?.[0]?.valueString;
        if (!description) return;

        const normalizedKey = description.trim().toLowerCase();

        // Ignora se já existe em FHIR (prioridade)
        if (fhirDescriptions.has(normalizedKey)) return;

        const hasStatus = !!problem?.item?.[1]?.answer?.[0]?.valueCoding;

        // Extrai dados do profissional
        const displayReferences = resource?.item?.find(
          (item: any) => item?.linkId === "displayReferences"
        )?.item;

        const practitionerName =
          (displayReferences?.[1]?.answer?.[0]?.valueString || "").toUpperCase();

        const problemItem: ProblemItem = {
          encounterId,
          encounterRef,
          param: "-",
          description,
          date: dayjs(authored).format("DD/MM/YYYY HH:mm"),
          dateObj: new Date(authored),
          practitioner: practitionerName,
          informant: "Questionário",
          isCustom: true,
          hasStatus,
        };

        // Adiciona ao grupo
        if (!groups[normalizedKey]) {
          groups[normalizedKey] = [];
        }
        groups[normalizedKey].push(problemItem);
      });
    });

    return groups;
  }

  /**
   * Aplica lógica par/ímpar para seleção de problemas
   * - PAR: mostra 1 registro (com status ou mais recente)
   * - ÍMPAR: mostra 2 registros (um com status + um sem status)
   */
  function applyEvenOddLogic(
    problemGroups: Record<string, ProblemItem[]>
  ): ProblemItem[] {
    const result: ProblemItem[] = [];

    Object.values(problemGroups).forEach((problems) => {
      if (problems.length === 1) {
        result.push(problems[0]);
        return;
      }

      // Ordena por data (mais recente primeiro)
      const sorted = problems.sort(
        (a, b) => b.dateObj.getTime() - a.dateObj.getTime()
      );

      const isPair = problems.length % 2 === 0;
      const withStatus = sorted.filter((p) => p.hasStatus);
      const withoutStatus = sorted.filter((p) => !p.hasStatus);

      if (isPair) {
        // PAR: apenas 1 registro
        if (withStatus.length > 0) {
          result.push(withStatus[0]); // Mais recente com status
        } else {
          result.push(sorted[0]); // Mais recente
        }
      } else {
        // ÍMPAR: até 2 registros
        if (withStatus.length > 0) {
          result.push(withStatus[0]); // Mais recente com status
          if (withoutStatus.length > 0) {
            result.push(withoutStatus[0]); // Mais recente sem status
          }
        } else {
          result.push(sorted[0]); // Mais recente
        }
      }
    });

    return result;
  }

  // ============= Processamento dos Dados =============

  // Processa dados FHIR (prioridade)
  const fhirProblems = processFhirData(data);

  // Coleta descrições dos dados FHIR para evitar duplicações
  const fhirDescriptions = extractDescriptions(fhirProblems);

  // Processa questionários (excluindo duplicações com FHIR)
  const questionnaireProblems = processQuestionnaireData(
    personData,
    fhirDescriptions
  );

  // Combina e ordena todos os dados
  const allProblems = [...fhirProblems, ...questionnaireProblems].sort(
    (a, b) => b.dateObj.getTime() - a.dateObj.getTime()
  );

  // ============= Renderização =============

  return (
    <EhrMobileGenericSection
      title={title}
      columns={columns}
      rows={allProblems}
      actions={
        access?.type === "patient"
          ? undefined
          : (rowData: any) => (
              <ProblemsActions
                row={rowData}
                openVisualizer={openVisualizer}
                verifyVisualizer={() => true}
              />
            )
      }
    />
  );
}
